import {
  type ElementType,
  forwardRef,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { Dialog, DialogTriggerProps } from '@/components/atoms';
import { cn } from '@/lib/utils/tailwind';

import type { AsElement } from '@/types';

type ModalTriggerProps<T extends ElementType = 'button'> =
  DialogTriggerProps<T>;

/*
required to create an inner & outer component due to radix needing forwardRef
and the fact we use generics
 */
const ModalTriggerBase = forwardRef<
  HTMLElement,
  {
    as?: ElementType;
    className?: string;
    children?: ReactNode;
  } & Omit<HTMLAttributes<HTMLElement>, 'ref' | 'className' | 'children'>
>(({ as, className, children, ...props }, ref) => {
  const Component: ElementType = as || 'button';

  return (
    <Dialog.Trigger
      as={Component}
      className={cn([], className)}
      ref={ref}
      {...props}
    >
      {children}
    </Dialog.Trigger>
  );
});
ModalTriggerBase.displayName = 'SheetTrigger';

const ModalTrigger = (<T extends ElementType = 'button'>(
  props: ModalTriggerProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
): ReactElement => {
  return <ModalTriggerBase {...props} />;
}) as <T extends ElementType = 'button'>(
  props: ModalTriggerProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
) => ReactElement;

Object.defineProperty(ModalTrigger, 'displayName', {
  value: 'ModalTrigger',
  configurable: true,
});

export { ModalTrigger, type ModalTriggerProps };
